import { api } from "@/lib/api/client";
import type {
  ChangeRequest,
  ChangeRequestUpload,
  Completeness,
  EmployeeProfile,
  ProfileFileKind,
} from "@/features/profile/types";

/** An employee's own pay, and when their annual leave salary comes. */
export interface MyPay {
  basic: string | null;
  allowances: string | null;
  /** The fourth component, optional: paid to whoever handles cash. */
  cash_risk: string | null;
  /** The third pay component, and what HR said it was for. */
  other_pay: string | null;
  other_pay_note: string | null;
  gross: string | null;
  bank_name: string | null;
  iban: string | null;
  date_of_joining: string | null;
  /** 1 to 12, the month the leave salary is paid in. */
  leave_salary_month: number | null;
  next_leave_salary_on: string | null;
  /** HR's figure when they have set one, otherwise a month's pay by the basis. */
  leave_salary_amount: string | null;
  /** Which of those the amount is. */
  leave_salary_source: "hr" | "basis";
  leave_salary_basis: "gross" | "basic";
  last_leave_salary_on: string | null;
  /** Annual leave days are unpaid on the sheet because the salary came in advance. */
  annual_leave_unpaid: boolean;
  /**
   * The GPSSA pension contribution taken from a UAE national's pay each month.
   * Null for everybody else, and until HR has set it.
   */
  gpssa_deduction?: string | null;
}

export const profileKeys = {
  all: ["profile"] as const,
  mine: () => [...profileKeys.all, "me"] as const,
  pay: () => [...profileKeys.all, "pay"] as const,
  completeness: () => [...profileKeys.all, "completeness"] as const,
  changeRequests: () => [...profileKeys.all, "change-requests"] as const,
  departments: () => ["departments"] as const,
};

export const profileApi = {
  get: () => api.get<EmployeeProfile>("/me/profile"),

  /** The departments HR lists, to choose from at sign-up. */
  departments: () => api.get<{ departments: string[] }>("/departments"),

  pay: () => api.get<MyPay>("/me/pay"),

  update: (changes: Record<string, unknown>) =>
    api.patch<EmployeeProfile>("/me/profile", changes),

  /** The fields HR crossed on an approved profile, straight on the record. */
  saveCorrections: (changes: Record<string, unknown>) =>
    api.patch<EmployeeProfile>("/me/corrections", changes),

  sendCorrectionsBack: () => api.post<{ message: string }>("/me/corrections/send-back"),

  completeness: () => api.get<Completeness>("/me/profile/completeness"),

  submit: () => api.post<{ message: string }>("/me/profile/submit"),

  uploadFile: (kind: ProfileFileKind, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return api.post<EmployeeProfile>(`/me/profile/files/${kind}`, body);
  },

  removeFile: (kind: ProfileFileKind) =>
    api.delete<EmployeeProfile>(`/me/profile/files/${kind}`),

  /** A signature drawn on the screen, as a PNG data URL. Onboarding only. */
  saveSignature: (dataUrl: string) =>
    api.post<EmployeeProfile>("/me/profile/signature", { data_url: dataUrl }),

  acceptDeclaration: (version = "1.0") =>
    api.post<EmployeeProfile>("/me/profile/declaration", { accepted: true, version }),

  listChangeRequests: () => api.get<ChangeRequest[]>("/me/change-requests"),

  /**
   * Stores a document for a change request without touching the record.
   *
   * An approved profile only changes when HR applies a request, so a new
   * scan or the bank's IBAN letter is uploaded here first and its id goes in
   * the request's changes.
   */
  uploadForChangeRequest: (kind: ProfileFileKind, file: File) => {
    const body = new FormData();
    body.append("file", file);
    return api.post<ChangeRequestUpload>(`/me/change-requests/files/${kind}`, body);
  },

  requestChange: (changes: Record<string, unknown>) =>
    api.post<ChangeRequest[]>("/me/change-requests", { changes }),

  withdrawChangeRequest: (id: string) =>
    api.post<{ message: string }>(`/me/change-requests/${id}/withdraw`),
};
